import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Button } from "@mui/material";
import { useTranslation } from "../../../hooks/useTranslation";
import { StateType } from "../../../backend/store";
import { authPasswordActions } from "../../../backend/store/reducers/authReducer";
import { PasswordFields } from "../PasswordFields";
import { isPublicNext } from "../../../next/public/mode";
import { SetPasswordDialog } from "../../../next/public/SetPasswordDialog";

// Mirrors App\Rules\User\PasswordRule on the backend.
const MIN_PASSWORD_LENGTH = 8;

/** Public v3 (P4.4): the new look draws the prompt itself; today's stays on the old line. */
export const SetPasswordModal: React.FC = () => (isPublicNext() ? <SetPasswordDialog /> : <LegacySetPasswordModal />);

const LegacySetPasswordModal: React.FC = () => {
  const { p } = useTranslation("workspaceSettings");
  const dispatch = useDispatch();

  const open = useSelector((state: StateType) => state.authStore.showSetPasswordModal);
  const saving = useSelector((state: StateType) => state.authStore.passwordSaving);
  const serverError = useSelector((state: StateType) => state.authStore.passwordError);

  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");

  if (!open) {
    return null;
  }

  const mismatchError = confirmation.length > 0 && password !== confirmation ? p("password_mismatch_error") : null;
  const canSubmit = password.length >= MIN_PASSWORD_LENGTH && password === confirmation;

  const handleSkip = () => {
    dispatch(authPasswordActions.closeSetPasswordModal());
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    dispatch(authPasswordActions.updatePassword({ password, password_confirmation: confirmation }));
  };

  return (
    <Dialog open={open} onClose={handleSkip} maxWidth="sm" fullWidth>
      <DialogTitle>{p("set_password_modal_title")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {p("set_password_modal_description")}
        </Typography>
        <PasswordFields
          password={password}
          onPasswordChange={setPassword}
          confirmation={confirmation}
          onConfirmationChange={setConfirmation}
          error={mismatchError || serverError}
        />
      </DialogContent>
      <DialogActions>
        <Button data-testid="skip-set-password-button" onClick={handleSkip} disabled={saving}>
          {p("skip_for_now_button")}
        </Button>
        <Button
          data-testid="submit-set-password-button"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!canSubmit || saving}
        >
          {p("create_password_button")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
